<!-- PatientComponentGallery.vue -->
<script setup lang="ts">
import { ref, reactive, StyleValue } from 'vue';
import { popAlert } from '../lib/Notification';
import PatientHeader from '../components/Choice/Patient/PatientHeader.vue'
import RadioButtonInput from '../components/RadioButtonInput.vue';
import SectionPanel from '../components/SectionPanel.vue';
import TextInput from '../components/TextInput.vue';
import MessageBadge from '../components/Messages/MessageBadge.vue';
import { PortalType } from '../Enums/ChoiceEnums';
import { DisplayStyle, LayoutOrientation } from '../Enums/UiEnums';
import PatientFlagBadges from '../components/Choice/Patient/PatientFlagBadges.vue';
import PatientPortal_MiscellaneousScreen from '../ChoiceScreens/PatientSetup/Miscellaneous/PatientPortal_MiscellaneousScreen.vue';
import ActionButtonV2 from '../components/ActionButtonV2.vue';
import { ActionButtonStyle } from '../lib/Types/Types';
import MessageText from '../components/Messages/MessageText.vue';

    const compData = reactive<{
        patientEntityId: number,
        hoveringOverHeader?: boolean,
        mouseOverHeader?:boolean,
        toolTipStyle?: StyleValue,
        hoverHideDelayMilliSec: number,
        patientHeaderContext: PortalType,
        patientId: number,
        patientMiscScreenDataChanges?:boolean
    }>({
        patientEntityId: 54241,
        hoverHideDelayMilliSec: 500,
        patientHeaderContext: PortalType.Scheduler,
        patientId: 10496
    });

    const miscellaneousScreen = ref<InstanceType<typeof PatientPortal_MiscellaneousScreen>>();

    async function onClickMML(patientEid: number)
    {
        await popAlert('patientEid: ' + patientEid);
    }

    function onMouseHover(hover:boolean, toolTipStyle?: StyleValue)
    {
        compData.hoveringOverHeader = hover;
        compData.toolTipStyle = toolTipStyle;
    }

    const patientDetails : Record<number, {appointmentId?: number}> = {
        [54241]: { appointmentId: 75777 }
    };

    async function savePatientMiscValues()
    {
        await miscellaneousScreen.value?.save();
    }

</script>

<template>
    <b>Patient Components:</b>

    <SectionPanel title="Patient Header">
        <div class="row">
            <div class="col">
                <RadioButtonInput :value="54241" v-model="compData.patientEntityId">
                    312213: Male, MML Temp Connection
                    <MessageBadge>w/TeleHealth Appointment</MessageBadge>
                </RadioButtonInput>
                <RadioButtonInput :value="52072" v-model="compData.patientEntityId">
                    311786: Male, MML Connected, CKCC Status=4
                    <MessageBadge>RPM Enrolled</MessageBadge>
                    <MessageBadge>Patient Flags</MessageBadge>
                </RadioButtonInput>
                <RadioButtonInput :value="54528" v-model="compData.patientEntityId">
                    312293: Male, MML Not Connected, CKCC Status=1
                </RadioButtonInput>
                <RadioButtonInput :value="54518" v-model="compData.patientEntityId">
                    312290: Female, MML Not Connected, CKCC Status=3
                </RadioButtonInput>
                <RadioButtonInput :value="51350" v-model="compData.patientEntityId">
                    311613: >1 Care Team Members
                </RadioButtonInput>
            </div>
        </div>
        <div class="row">
            <div class="col-sm-2">
                Hide Tool Tip Delay (ms)
            </div>
            <div class="col-sm-10">
                <TextInput v-model:textValue="compData.hoverHideDelayMilliSec" />
            </div>
        </div>        
        <div class="row">
            <div class="col-sm-2">
                Context
            </div>
            <div class="col-sm-10">
                <RadioButtonInput :value="PortalType.Billing" v-model:model-value="compData.patientHeaderContext" :layout="LayoutOrientation.Horz">
                    Billing
                </RadioButtonInput>
                <RadioButtonInput :value="PortalType.Emr" v-model:model-value="compData.patientHeaderContext" :layout="LayoutOrientation.Horz">
                    EMR
                </RadioButtonInput>
                <RadioButtonInput :value="PortalType.Scheduler" v-model:model-value="compData.patientHeaderContext" :layout="LayoutOrientation.Horz">
                    Scheduler
                </RadioButtonInput>
                <RadioButtonInput :value="PortalType.Documents" v-model:model-value="compData.patientHeaderContext" :layout="LayoutOrientation.Horz">
                    Documents
                </RadioButtonInput>
            </div>
        </div>

        <hr/>

        <div class="text-end" style="font-size: x-small">
            <table>
                <tbody>
                    <tr>
                        <td style="width:60%; background-color: darkgray;"></td>
                        <td>
                            <PatientHeader 
                                :displayContext="compData.patientHeaderContext"
                                :legacyPage="false"
                                :patientEntityId="compData.patientEntityId" 
                                :appointmentId="patientDetails[compData.patientEntityId]?.appointmentId"
                                @clickMML="onClickMML" 
                                @mouseHover="onMouseHover"
                                @mouseOverHeader="over => compData.mouseOverHeader = over"
                                :mouseOutDelayMilliseconds="compData.hoverHideDelayMilliSec" />        
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </SectionPanel>

    <SectionPanel title="Patient Flag Badges">
        <PatientFlagBadges :patientEntityId="compData.patientEntityId" :displayContext="PortalType.Scheduler" />
    </SectionPanel>

    <SectionPanel title="Setup: Miscellaneous">
        <MessageBadge>
            PatientId: {{ compData.patientId }}
        </MessageBadge>
        <PatientPortal_MiscellaneousScreen ref="miscellaneousScreen" :patientId="compData.patientId" @dataChanged="dataChanaged => compData.patientMiscScreenDataChanges = dataChanaged"/>

        <template v-if="compData.patientMiscScreenDataChanges">
            <ActionButtonV2 :action="ActionButtonStyle.Save" :performAction="savePatientMiscValues" />
        </template>
        <template v-if="!compData.patientMiscScreenDataChanges">
            <MessageText :displayStyle="DisplayStyle.Danger">
                No changes to save for Misc Patient Data
            </MessageText>
        </template>
    </SectionPanel>
    <br/>
    <br/>
    <br/>

</template>